import { captureTtclidFromUrl, getTtclid } from '../utils/ttclid';

// Define the TikTok tracking queue interface
interface TikTokTrackingQueue {
  methods: string[];
  setAndDefer: (t: any, e: string) => void;
  instance: (t: string) => any;
  load: (pixelId: string, options?: any) => void;
  page: () => void;
  track: (event: string, data?: any) => void;
  _i: Record<string, any>;
  _t: Record<string, number>;
  _o: Record<string, any>;
  [key: string]: any;
}

declare global {
  interface Window {
    TiktokAnalyticsObject: string;
    ttq: TikTokTrackingQueue;
  }
}

const PIXEL_ID = 'D38FSDRC77UFKOQ7JJF0';
let isTikTokInitialized = false;

// Modify initTikTokPixel to capture ttclid
export const initTikTokPixel = (): void => {
  if (typeof window === 'undefined' || isTikTokInitialized) {
    return;
  }

  const w = window;
  const d = document;
  const t = 'ttq';

  w.TiktokAnalyticsObject = t;
  const ttq = (w[t] = w[t] || []);
  ttq.methods = [
    'page',
    'track',
    'identify',
    'instances',
    'debug',
    'on',
    'off',
    'once',
    'ready',
    'alias',
    'group',
    'enableCookie',
    'disableCookie',
    'holdConsent',
    'revokeConsent',
    'grantConsent'
  ];

  ttq.setAndDefer = function(t, e) {
    t[e] = function() {
      t.push([e].concat(Array.prototype.slice.call(arguments, 0)));
    };
  };

  for (let i = 0; i < ttq.methods.length; i++) {
    ttq.setAndDefer(ttq, ttq.methods[i]);
  }

  ttq.instance = function(t) {
    const e = ttq._i[t] || [];
    for (let n = 0; n < ttq.methods.length; n++) {
      ttq.setAndDefer(e, ttq.methods[n]);
    }
    return e;
  };

  ttq.load = function(e, n) {
    const r = 'https://analytics.tiktok.com/i18n/pixel/events.js';
    ttq._i = ttq._i || {};
    ttq._i[e] = [];
    ttq._i[e]._u = r;
    ttq._t = ttq._t || {};
    ttq._t[e] = +new Date();
    ttq._o = ttq._o || {};
    ttq._o[e] = n || {};

    const script = d.createElement('script');
    script.type = 'text/javascript';
    script.async = true;
    script.src = `${r}?sdkid=${e}&lib=${t}`;
    
    const firstScript = d.getElementsByTagName('script')[0];
    firstScript?.parentNode?.insertBefore(script, firstScript);
  };

  // Capture ttclid from URL and store in cookie (7-day attribution window)
  captureTtclidFromUrl(7);

  // Initialize the pixel
  ttq.load(PIXEL_ID);
  ttq.page();
  isTikTokInitialized = true;
};

export const updateTikTokConsent = (hasConsent: boolean): void => {
  if (typeof window === 'undefined') return;

  if (hasConsent) {
    initTikTokPixel();
    if (window.ttq?.grantConsent) window.ttq.grantConsent();
    if (window.ttq?.enableCookie) window.ttq.enableCookie();
    return;
  }

  if (window.ttq?.revokeConsent) window.ttq.revokeConsent();
  if (window.ttq?.disableCookie) window.ttq.disableCookie();
};

// Helper function to validate and format email for TikTok pixel
function validateAndFormatEmail(email: string): string | null {
  if (!email) return null;

  // Convert to lowercase and trim
  const formattedEmail = email.toLowerCase().trim();

  // Check for invalid placeholder values
  const invalidValues = ['null', 'undefined', 'redacted', 'mail@example.com'];
  if (invalidValues.includes(formattedEmail)) return null;

  // Basic email format validation
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  if (!emailRegex.test(formattedEmail)) return null;

  return formattedEmail;
}

// Helper function to hash data with SHA-256
async function hashData(data: string): Promise<string | null> {
  try {
    // Check if crypto.subtle is available (requires HTTPS or localhost)
    if (!crypto || !crypto.subtle) {
      console.warn('crypto.subtle not available - TikTok pixel hashing disabled');
      return null;
    }
    
    const encoder = new TextEncoder();
    const dataBuffer = encoder.encode(data);
    const hashBuffer = await crypto.subtle.digest('SHA-256', dataBuffer);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
  } catch (error) {
    console.error('Error hashing data for TikTok pixel:', error);
    return null;
  }
}

// Interface for user identification data
interface UserIdentification {
  email?: string;
  phoneNumber?: string;
  externalId?: string;
}

// Function to identify user
export async function identifyTikTokUser(userData: UserIdentification): Promise<void> {
  if (!window.ttq) return;

  const identifyData: Record<string, string> = {};
  
  if (userData.email) {
    const validEmail = validateAndFormatEmail(userData.email);
    if (validEmail) {
      const hashedEmail = await hashData(validEmail);
      if (hashedEmail) {
        identifyData.email = hashedEmail;
      }
    }
  }
  if (userData.phoneNumber) {
    const hashedPhone = await hashData(userData.phoneNumber);
    if (hashedPhone) {
      identifyData.phone_number = hashedPhone;
    }
  }
  if (userData.externalId) {
    const hashedId = await hashData(userData.externalId);
    if (hashedId) {
      identifyData.external_id = hashedId;
    }
  }

  // Only call identify if we have at least one hashed field
  if (Object.keys(identifyData).length > 0) {
    window.ttq.identify(identifyData);
  }
}

// Enhanced complete registration tracking
export async function trackTikTokSignUp(data: { 
  email: string;
  userId: string;
  content_type: string;
  has_instagram: boolean;
}): Promise<void> {
  if (!window.ttq) return;

  // Validate email before sending
  const validEmail = validateAndFormatEmail(data.email);
  if (!validEmail) {
    console.warn('Invalid email format provided to TikTok pixel tracking');
    return;
  }

  // First identify the user
  await identifyTikTokUser({
    email: validEmail,
    externalId: data.userId
  });

  // Retrieve stored ttclid for attribution
  const ttclid = getTtclid();
  
  // Track the registration event
  window.ttq.track('CompleteRegistration', {
    contents: [{
      content_id: data.userId,
      content_type: 'product',
      content_name: data.has_instagram ? 'User Registration with Instagram' : 'User Registration'
    }],
    value: 1,
    currency: 'EUR',
    ...(ttclid && { ttclid }) // Include ttclid for attribution
  });
}

// Track contact/request events
export function trackTikTokContact(data: {
  content_type: 'flash' | 'custom';
  value: number;
  currency: string;
  requestId?: string;
}): void {
  if (!window.ttq) return;

  // Retrieve stored ttclid for attribution
  const ttclid = getTtclid();

  window.ttq.track('Contact', {
    contents: [{
      content_id: data.requestId || 'contact',
      content_type: 'product',
      content_name: `${data.content_type === 'flash' ? 'Flash' : 'Custom'} Tattoo Inquiry`
    }],
    value: data.value,
    currency: data.currency,
    ...(ttclid && { ttclid }) // Include ttclid for attribution
  });
}
